/* eslint-disable no-shadow */
/* eslint-disable react/jsx-props-no-spreading */
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { yesNo } from '../helpers';
import Input from '../form/Input';
import BottomBar from '../BottomBar';
import Dropdown from '../form/Dropdown';
import RadioButton from '../form/RadioButton';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { COUNTRY } from '../../../environment';
import {
  createGoverningBodies,
  fetchSupervision,
  updateSupervision,
} from '../../../actions/preferences';

const ProfessionalDetails3 = ({
  createGoverningBodies,
  fetchSupervision,
  updateSupervision,
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  currentStep,
  previousPage,
  states,
  draftValues,
  onDraftChange,
}) => {
  const intl = useIntl();
  const initialFieldNumber = useMemo(() => {
    const keys = Object.keys(draftValues || {}).filter(
      key =>
        key.startsWith('governing_jurisdiction_') ||
        key.startsWith('governing_body_') ||
        key.startsWith('license_number_'),
    );

    if (!keys.length) {
      return 1;
    }

    const maxIndex = Math.max(
      ...keys.map(key => Number(key.split('_').pop())).filter(Number.isFinite),
    );

    return Number.isFinite(maxIndex) ? maxIndex + 1 : 1;
  }, [draftValues]);

  const [fieldNumber, setFieldNumber] = useState(initialFieldNumber);
  const [supervisionYN, setSupervisionYN] = useState(
    // eslint-disable-next-line no-nested-ternary
    draftValues?.clinical_supervision?.supervision === true ||
      draftValues?.clinical_supervision?.supervision === 'true'
      ? true
      : draftValues?.clinical_supervision?.supervision === false ||
        draftValues?.clinical_supervision?.supervision === 'false'
      ? false
      : null,
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: draftValues || {
      clinical_supervision: {
        supervision: null,
        receipt_text: '',
      },
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  // Fetch supervision data on mount
  useEffect(() => {
    if (draftValues?.clinical_supervision?.supervision !== undefined) {
      return;
    }

    fetchSupervision(resp => {
      const { clinical_supervision: clinicalSupervision } = resp.data;
      setSupervisionYN(!_.isEmpty(clinicalSupervision));
      if (!_.isEmpty(clinicalSupervision)) {
        setValue('clinical_supervision.supervision', true);
        setValue(
          'clinical_supervision.receipt_text',
          clinicalSupervision.receipt_text || '',
        );
      }
    });
  }, [fetchSupervision, setValue, draftValues]);

  const onSubmit = async values => {
    const errorMessage = intl.formatMessage({
      defaultMessage: 'There was a problem updating your professional details.',
    });

    // Build governing bodies payload and remove empty fields
    const governingBodies = [];
    _.each(Object.keys(values), field => {
      if (field.includes('governing_jurisdiction_')) {
        const i = field.substr(field.lastIndexOf('_') + 1);

        const body = {
          state: values[field],
          governing_body: values[`governing_body_${i}`],
          license_number: values[`license_number_${i}`],
        };

        // only push if at least one field is non-empty
        if (Object.values(body).some(v => v && String(v).trim() !== '')) {
          governingBodies.push(body);
        }
      }
    });
    const governingBodyValues = { governing_bodies: governingBodies };

    // Build supervision payload
    // eslint-disable-next-line camelcase
    const { supervision, receipt_text } = values.clinical_supervision;
    const supervisionValues = {
      supervision,
      info_receipts_yn: true,
      receipt_text,
    };

    openLoadingPanel(
      intl.formatMessage({
        defaultMessage: 'Updating professional details...',
      }),
    );
    try {
      await Promise.all([
        createGoverningBodies(governingBodyValues),
        updateSupervision(supervisionValues),
      ]);
      closeLoadingPanel();
      nextPage();
    } catch (error) {
      errorCallback(error, errorMessage);
    }
  };

  const addField = () => setFieldNumber(prev => prev + 1);

  const renderInputs = () =>
    _.times(fieldNumber, i => (
      <div className="input-row" key={i} style={{ alignItems: 'center' }}>
        <Controller
          name={`governing_jurisdiction_${i}`}
          control={control}
          rules={{
            validate: value => {
              const {
                [`governing_body_${i}`]: body,
                [`license_number_${i}`]: license,
              } = getValues();
              if (value || body || license) {
                return value
                  ? true
                  : intl.formatMessage({ defaultMessage: 'Required' });
              }
              return true; // all empty → no error
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <Dropdown
              {...field}
              options={states}
              label={intl.formatMessage({
                defaultMessage: 'Governing Jurisdiction',
              })}
              error={error?.message}
            />
          )}
        />
        <Controller
          name={`governing_body_${i}`}
          control={control}
          rules={{
            validate: value => {
              const {
                [`governing_jurisdiction_${i}`]: jurisdiction,
                [`license_number_${i}`]: license,
              } = getValues();
              if (value || jurisdiction || license) {
                return value
                  ? true
                  : intl.formatMessage({ defaultMessage: 'Required' });
              }
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label={intl.formatMessage({ defaultMessage: 'Governing Body' })}
              error={error?.message}
            />
          )}
        />
        <Controller
          name={`license_number_${i}`}
          control={control}
          rules={{
            validate: value => {
              const {
                [`governing_jurisdiction_${i}`]: jurisdiction,
                [`governing_body_${i}`]: body,
              } = getValues();
              if (value || jurisdiction || body) {
                return value
                  ? true
                  : intl.formatMessage({ defaultMessage: 'Required' });
              }
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label={intl.formatMessage({
                defaultMessage: 'License/Registration Number',
              })}
              error={error?.message}
            />
          )}
        />
        {fieldNumber - 1 === i && (
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          <button className="add-more-button" type="button" onClick={addField}>
            +
          </button>
        )}
      </div>
    ));

  const renderClinicalSupervision = () => (
    <div
      className="input-row"
      style={{ flexDirection: 'column', marginBottom: '40px' }}
    >
      <h3>
        {intl.formatMessage({
          defaultMessage: 'Are you practicing under a clinical supervisor?',
        })}
      </h3>
      <Controller
        name="clinical_supervision.supervision"
        control={control}
        rules={{
          required: intl.formatMessage({
            defaultMessage:
              'Please indicate whether or not you are practicing under a clinical supervisor',
          }),
        }}
        render={({ field }) => (
          <RadioButton
            {...field}
            options={yesNo}
            onChange={value => {
              field.onChange(value);
              setSupervisionYN(JSON.parse(value));
            }}
          />
        )}
      />
      {errors?.clinical_supervision?.supervision && (
        <p className="onboarding-error">
          {errors.clinical_supervision.supervision.message}
        </p>
      )}

      {supervisionYN && (
        <div>
          <h6>
            {intl.formatMessage({
              defaultMessage:
                'Please enter the clinical supervision information (e.g. supervisor name and college number) as it should appear on session receipts for your clients who request it.',
            })}
          </h6>
          <Controller
            name="clinical_supervision.receipt_text"
            control={control}
            rules={{
              required: intl.formatMessage({
                defaultMessage:
                  "Please enter what should appear on client's receipts.",
              }),
            }}
            render={({ field, fieldState: { error } }) => (
              <Input
                className="onboarding-text"
                style={{ marginBottom: 40, width: 220 }}
                {...field}
                error={error?.message}
              />
            )}
          />
        </div>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>
          {intl.formatMessage({ defaultMessage: 'Professional Details' })}
        </h1>
        <div className="sub-page-indicator">
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {`3/${COUNTRY === 'US' ? '4' : '3'}`}
        </div>
      </div>
      <h3 style={{ marginBottom: '25px' }}>
        {intl.formatMessage(
          {
            defaultMessage:
              'Which {provinceOrState} are you licensed/registered with? Please provide the corresponding governing body and license/registration number.',
          },
          { provinceOrState: COUNTRY === 'US' ? 'state(s)' : 'province(s)' },
        )}
      </h3>
      {renderInputs()}
      <div style={{ marginBottom: '40px' }}></div>
      {renderClinicalSupervision()}
      <BottomBar
        currentStep={currentStep}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </form>
  );
};

ProfessionalDetails3.propTypes = {
  closeLoadingPanel: PropTypes.func.isRequired,
  createGoverningBodies: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  errorCallback: PropTypes.func.isRequired,
  fetchSupervision: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  openLoadingPanel: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  states: PropTypes.array.isRequired,
  updateSupervision: PropTypes.func.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

const mapStateToProps = state => ({
  states: state.misc.states,
});

export default connect(mapStateToProps, {
  createGoverningBodies,
  fetchSupervision,
  updateSupervision,
})(ProfessionalDetails3);
